<script setup lang="ts">
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import api from '@/api';
import { useProfilesStore } from '@/stores/riskProfileStore';
import type { ProfileGroup, ProfileGroupDeleteImpact } from '@/types/risk-profile';

type DeleteImpactTone = 'critical' | 'soft';

const emit = defineEmits<{
    deleted: [];
}>();

const confirm = useConfirm();
const toast = useToast();
const store = useProfilesStore();

const getEntityLabel = (entityType: ProfileGroup['entity_type'], affectedEntityCount: number) => {
    if (entityType === 'company') {
        return affectedEntityCount === 1 ? 'empresa' : 'empresas';
    }

    return affectedEntityCount === 1 ? 'pessoa' : 'pessoas';
};

const buildDeleteConfirmationContent = (
    group: ProfileGroup,
    impact?: ProfileGroupDeleteImpact
) => {
    const reportCount = impact?.reportCount ?? 0;
    const affectedEntityCount = impact?.affectedEntityCount ?? 0;
    const entityLabel = getEntityLabel(impact?.entity_type ?? group.entity_type, affectedEntityCount);
    const hasReports = reportCount > 0;

    return {
        title: hasReports ? 'Esta exclusão tem impacto em relatórios existentes' : 'Excluir política de risco?',
        description: hasReports
            ? 'Os relatórios já gerados continuarão disponíveis, mas a política será arquivada para novas utilizações.'
            : 'A exclusão é lógica e preserva o histórico já existente.',
        impactLabel: hasReports ? 'Impacto detectado' : 'Ação reversível apenas via suporte técnico',
        impactMessage: hasReports
            ? `${reportCount} relatório(s) utilizam esta política, distribuídos em ${affectedEntityCount} ${entityLabel}.`
            : 'A política deixará de aparecer em novos fluxos, cadastros e execuções futuras.',
        tone: hasReports ? 'critical' as DeleteImpactTone : 'soft' as DeleteImpactTone,
        profileName: impact?.profileName ?? group.profileName,
        reportCount,
        affectedEntityCount,
        entityLabel,
    };
};

const executeDeleteProfile = async (profileGroupId: string) => {
    try {
        await store.deleteProfile(profileGroupId);
        toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Política excluída com sucesso.', life: 3000 });
        emit('deleted');
    } catch (err: any) {
        const detail = err.response?.data?.detail || 'Não foi possível excluir a política.';
        toast.add({ severity: 'error', summary: 'Erro', detail, life: 5000 });
    }
};

const showDeleteConfirmation = (group: ProfileGroup, impact?: ProfileGroupDeleteImpact) => {
    const content = buildDeleteConfirmationContent(group, impact);

    confirm.require({
        group: 'profile-delete-confirmation',
        message: content.description,
        header: 'Confirmar exclusão',
        rejectLabel: 'Cancelar',
        acceptLabel: 'Excluir política',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        ...content,
        accept: async () => {
            await executeDeleteProfile(group.profileGroupId);
        }
    });
};

const open = (group: ProfileGroup) => {
    api.accountAdmin.getProfileGroupDeleteImpact(group.profileGroupId)
        .then((impact) => {
            showDeleteConfirmation(group, impact);
        })
        .catch(() => {
            showDeleteConfirmation(group);
        });
};

defineExpose({ open });
</script>

<template>
  <ConfirmDialog
    group="profile-delete-confirmation"
    :draggable="false"
    :style="{ width: 'min(34rem, calc(100vw - 2rem))' }"
    :breakpoints="{ '960px': '92vw', '640px': '96vw' }"
  >
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="overflow-hidden rounded-3xl border border-red-100 bg-white shadow-2xl shadow-red-900/10">
        <div class="h-1.5 bg-gradient-to-r from-red-500 via-rose-500 to-amber-400" />
        <div class="p-6 sm:p-7">
          <div class="flex items-start gap-4">
            <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-100">
              <BaseIcon size="lg">delete_forever</BaseIcon>
            </div>
            <div class="min-w-0 flex-1">
              <div class="mb-2 flex flex-wrap items-center gap-2">
                <span
                  class="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
                  :class="message.tone === 'critical' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'"
                >
                  {{ message.impactLabel }}
                </span>
              </div>
              <h2 class="text-2xl font-semibold leading-tight text-slate-900">
                {{ message.title }}
              </h2>
              <p class="mt-2 text-sm leading-6 text-slate-600">
                {{ message.description }}
              </p>
            </div>
          </div>

          <div class="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Política selecionada
            </div>
            <div class="mt-2 flex items-center justify-between gap-3">
              <div class="min-w-0 truncate text-lg font-semibold text-slate-900">
                {{ message.profileName }}
              </div>
              <div class="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                {{ message.reportCount > 0 ? 'Em uso' : 'Sem relatórios vinculados' }}
              </div>
            </div>
          </div>

          <div
            class="mt-4 rounded-2xl p-4"
            :class="message.tone === 'critical' ? 'bg-red-50 text-red-900 ring-1 ring-red-100' : 'bg-amber-50 text-amber-900 ring-1 ring-amber-100'"
          >
            <div class="flex items-start gap-3">
              <BaseIcon class="mt-0.5 shrink-0" size="sm">
                {{ message.tone === 'critical' ? 'warning' : 'info' }}
              </BaseIcon>
              <div>
                <div class="text-sm font-semibold">
                  Impacto da exclusão
                </div>
                <p class="mt-1 text-sm leading-6">
                  {{ message.impactMessage }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="message.reportCount > 0" class="mt-4 grid gap-3 sm:grid-cols-2">
            <div class="rounded-2xl border border-slate-200 bg-white p-4">
              <div class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Relatórios afetados
              </div>
              <div class="mt-2 text-2xl font-semibold text-slate-900">
                {{ message.reportCount }}
              </div>
            </div>
            <div class="rounded-2xl border border-slate-200 bg-white p-4">
              <div class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {{ message.entityLabel === 'empresa' || message.entityLabel === 'empresas' ? 'Empresas impactadas' : 'Pessoas impactadas' }}
              </div>
              <div class="mt-2 text-2xl font-semibold text-slate-900">
                {{ message.affectedEntityCount }}
              </div>
            </div>
          </div>

          <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              label="Cancelar"
              severity="secondary"
              outlined
              class="w-full sm:w-auto"
              @click="rejectCallback"
            />
            <Button
              label="Excluir política"
              severity="danger"
              class="w-full sm:w-auto"
              @click="acceptCallback"
            >
              <template #icon>
                <BaseIcon size="sm">delete</BaseIcon>
              </template>
            </Button>
          </div>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
